import { useState, useRef, useEffect } from 'react';
import { MapPin, Phone, Clock, Mail, Star } from 'lucide-react';
import { AnimatedSection } from '@/hooks/useScrollAnimation';
import WhatsAppCTA from '@/components/habitus/WhatsAppCTA';
import { useTrackEvent } from '@/hooks/useTrackEvent';
import { useWhatsAppLinkDaPagina } from '@/hooks/useWhatsAppLinkDaPagina';
import { CLINIC_INFO } from '@/data/constants';

// Vem de CLINIC_INFO (CID do Perfil da Empresa). O literal que estava aqui
// trazia um TERCEIRO par de coordenadas (-15.7996, -47.9286), diferente tanto
// do schema quanto da ficha real -- errava por ~500 m.
const GOOGLE_MAPS_URL = CLINIC_INFO.googleMaps;

// `cid` em vez de busca por texto: o mapa passa a mostrar a ficha exata da
// clínica, e não o que o Google achar para o nome.
const GOOGLE_MAPS_EMBED = `https://www.google.com/maps?cid=${CLINIC_INFO.googleMapsCid}&output=embed`;

const Contact = () => {
  const { trackEventBeforeNavigation } = useTrackEvent();
  // Esta seção aparece em todas as páginas: manda a mensagem da página atual.
  const whatsappLink = useWhatsAppLinkDaPagina();
  const [mapClicked, setMapClicked] = useState(false);
  const [mapVisible, setMapVisible] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMapVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleMapClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!mapClicked) {
      setMapClicked(true);
      trackEventBeforeNavigation('map_click', { source: 'contact_section' }, GOOGLE_MAPS_URL);
    } else {
      window.open(GOOGLE_MAPS_URL, '_blank');
    }
  };

  return (
    <AnimatedSection className="section-padding bg-secondary/30 overflow-hidden" id="contato">
      <div className="container-mobile">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            Contato
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-3">
            Venha nos Conhecer
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Estamos prontos para recebê-lo e iniciar o seu acompanhamento.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            {/* Address */}
            <div className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Endereço</h3>
                <p className="text-muted-foreground text-sm">
                  SHCSW lotes 3/4/5, St. Sudoeste<br />
                  Centro Clínico Sudoeste Sala 101<br />
                  Brasília - DF, 70673-416
                </p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Telefone</h3>
                {/* Ligacao vai no numero de VOZ (callPhone). O 94756-8717 e o
                    numero de WhatsApp e nao atende chamada -- quem clicava aqui
                    ligava para uma linha que nao toca. */}
                <a
                  href={`tel:+${CLINIC_INFO.callPhoneRaw}`}
                  className="text-muted-foreground text-sm hover:text-primary transition-colors"
                >
                  {CLINIC_INFO.callPhone}
                </a>
              </div>
            </div>

            {/* Hours */}
            <div className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Horário</h3>
                <p className="text-muted-foreground text-sm">
                  Segunda a Sexta de 8h às 18h
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">E-mail</h3>
                <a 
                  href="mailto:contato@clinicahabitus.com.br" 
                  className="text-muted-foreground text-sm hover:text-primary transition-colors"
                >
                  contato@clinicahabitus.com.br
                </a>
              </div>
            </div>

            {/* Avaliacoes: link para a ficha real no Google. Sem nota nem
                contagem no site (autoavaliacao e vedada pela Res. CFM 2.336 e
                conta como spam de dados estruturados). */}
            <div className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Star className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Avaliações</h3>
                <a
                  href={GOOGLE_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground text-sm hover:text-primary transition-colors"
                >
                  Ver avaliações de pacientes no Google
                </a>
              </div>
            </div>

            {/* CTA */}
            <WhatsAppCTA 
              href={whatsappLink} 
              source="contact_section"
              className="w-full bg-primary hover:bg-primary/90 text-lg py-6 h-auto"
            >
              Agendar Consulta pelo WhatsApp
            </WhatsAppCTA>
          </div>

          {/* Map - lazy loaded */}
          <div ref={mapRef} className="relative aspect-[4/3] sm:aspect-square lg:aspect-auto lg:min-h-[400px] rounded-2xl overflow-hidden bg-muted">
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleMapClick}
              className="absolute inset-0 z-10"
              aria-label="Abrir no Google Maps"
            />
            {mapVisible && (
              <iframe
                src={GOOGLE_MAPS_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '300px', pointerEvents: 'none' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização Clínica Habitus"
                className="absolute inset-0"
              />
            )}
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

export default Contact;
